import { VueConstructor } from 'vue';
import hash from 'object-hash';
import { Cell, cellNames, columnsToCamelCase, getColumnsRowMapObject, Row, EditableStatus } from '@/main/utils/table';
import { PageMode } from '@/main/utils/pageMode';
import { ListLoadBehavior } from '@/main/utils/listLoadBehavior';
import { END_LOADING, START_LOADING } from '@/main/store/listEditTypes';
import {
	FILTER, RESET, RESET_FILTER, TABLE_DATA,
	TABLE_SCHEMA, UPDATE_BASIC_FILTER, TABLE_DATA_EXPORT,
} from '@/main/store/listEditTypes';
import { openEdit } from '@/main/router/routerPath';
import { PAGE_MGR_MAPPER_DATA } from '@/main/store/actionTypes';
import { ListStoreBase } from '@/main/store/@types/list.d';
import { httpRequest } from '@/main/services/httpRequest';

import { Page } from './page';

interface LayoutPropsList {
	model: object,
	validationRules: object | null,
	operations: number,
	filterClick: () => void,
	filterResetClick: () => void,
	closeClick: () => void,
	createClick: () => void,
	exportClick: () => void,
	canClose: boolean,
	beginWithDisplayedFilter: boolean,
}

type PageWithListStore = InstanceType<typeof Page> & ListStoreBase;

/**
 * @abstract use `listFactory` to create List components.
 */
export const List = (Page as VueConstructor<PageWithListStore>).extend({
	name: 'List',
	props: {
		/** Called after row is selected in a SelectList component. */
		afterRowSelection: {
			type: Function,
			default: () => {
			},
		},
	},
	data() {
		return {
			/** Default list load behavior, overridable by openEdit. */
			listLoadDefault: ListLoadBehavior.AUTO_LOAD,
			schemaSettings: {
				isEditable: false,
			},
		};
	},
	computed: {
		/** Data for filter-layout. */
		layoutProps(): LayoutPropsList {
			return {
				model: this.filter,
				validationRules: this.validationRules,
				filterClick: this.filterClick,
				filterResetClick: this.filterResetClick,
				createClick: this.createClickFun,
				exportClick: this.exportFun,
				operations: this.table.operations,
				closeClick: this.closeClick,
				canClose: this.canClose,
				beginWithDisplayedFilter: this.listLoadBehavior !== ListLoadBehavior.HIDE_FILTER,
			};
		},
		listLoadBehavior(): ListLoadBehavior {
			return this.$route.params.listLoadBehavior as ListLoadBehavior || this.listLoadDefault;
		},
		autoLoadItems(): boolean {
			return this.listLoadBehavior !== ListLoadBehavior.NO_LOAD;
		},
		sideBarLoading(): boolean {
			return (this.rowDetail?.loadingCount ?? 0) > 0;
		},
		tableListeners(): {} {
			return {
				sideBarSelectRow: this.sideBarSelectRow,
				'save-column-setting': this.saveColumnSetting,
				selectedRow: this.selectedRow,
				'sort-change': this.saveSort,
			};
		},
		tableModel(): {} {
			return { table: this.table, sideBarLoading: this.sideBarLoading };
		},
		validator() {
			return (this.$children[0].$refs.mainForm as any).validate;
		},
		cellNames(): Record<string, number> {
			const { columns } = this.table;
			return cellNames({ columns });
		},
	},
	methods: {
		saveSort(sort: { columnId: any; order: string }) {
			this.$store.commit(`${this.storeUri}/SAVE_SORT`, { data: { ...sort }, tableName: '' });
		},
		sideBarSelectRow(cells: Cell[]) {
			this.rowDetail && this.$store.dispatch(`${this.storeUri}/ROW_DETAIL`, cells[cellNames(this.table).idColumn]);
		},
		/** Called when the check button is clicked. */
		filterClick() {
			 this.tableData();
		},
		setFilterByParams() {
			if (this.props.filter) {
				this.$store.commit(`${this.storeUri}/${UPDATE_BASIC_FILTER}`, { Filter: this.props.filter });
			}

			if (this.isExternal && this.storeUri) {
				this.setFiltersFromExternal(this.storeUri);
			}
		},
		/** Loads filter data, and list schema and data. */
		async loadData() {
			const schema = await this.schema();
			if(schema.message !== undefined)
			{
				return;	
			}
			await this.loadFilter();

			this.setFilterByParams();

			if (!this.autoLoadItems) return;

			const { data } = await this.tableData();

			if (this.table.computeHashOnLoad) {
				this.table.hash = hash(this.table.rows);
			}

			if (this.isSelectList && data.length === 1) {
				const rowData = getColumnsRowMapObject(columnsToCamelCase(schema.data.Columns), data[0].Cells);

				this.selectedRow({ data: rowData });
			}
		},
		refreshFun() {
			this.tableData();
		},
		async exportFun() {
			await this.$store.dispatch(`${this.storeUri}/${TABLE_DATA_EXPORT}`);
		},
		/**
		 * Called after row is selected in a SelectList component. Calls
		 * the {@link this.afterRowSelection} prop function.
		 */
		selectedRow(event: { data: any }) {
			if (this.isExternal) {
				this.openerSelect(event.data);
				return;
			}

			this.$store.commit(`pageManager/${PAGE_MGR_MAPPER_DATA}`, { mapperData: event.data }, { root: true });
			// TODO: call vuex mutation to mutate object
			(this.props.afterRowSelection as Function)({ row: event.data });
			this.$router.back();
		},
		/**
		 * Called after clicking the cmdVytvor button.
		 *
		 * Redirects website to Edit component for creating a new entity (new list row).
		 */
		createClickFun() {
			openEdit(
				// Remove the ending 'List' from component name.
				this.$options.name?.slice(0, -4) ?? '',
				PageMode.CREATE,
				{ props: true, filter: this.filter },
			);
		},
		// #region TableSchema
		/**
		 * Loads table schema, by dispatching the `TABLE_SCHEMA`
		 */
		schema() {
			return this.$store.dispatch(`${this.storeUri}/${TABLE_SCHEMA}`, { isSelect: this.isSelectList, schemaSettings: this.schemaSettings });
		},
		// #endregion

		// #region TableData
		/**
		 * Loads table data through `TABLE_DATA` store action.
		 */
		tableData() {
			return this.$store.dispatch(`${this.storeUri}/${TABLE_DATA}`, {
				filter: this.filter,
				isSelect: this.isSelectList,
				isEditable: this.schemaSettings.isEditable,
			});
		},
		// #endregion
		/** Commits {@link RESET} mutation on this component's state in store. */
		resetStore() {
			this.$store.commit(`${this.storeUri}/${RESET}`);
		},
		resetFilter() {
			this.$store.commit(`${this.storeUri}/${RESET_FILTER}`);
		},
		/** Executed after clicking the X button in List component */
		async filterResetClick() {
			this.resetFilter();
			(await this.loadFilter());
			this.filterClick();
		},
		/**
		 * Dispatches `FILTER` action.
		 *
		 * @returns data to initialize filter with.
		 */
		async loadFilter() {
			return (await this.$store.dispatch(`${this.storeUri}/${FILTER}`, this.isSelectList)).data;
		},
		// #endregion
		updateRow(rowUpdateData: { tableName: string, updateStatus: EditableStatus, rowId: number, row: Row }) {
			this.$store.commit(`${this.storeUri}/updateTableRow`, rowUpdateData);
		},
		updateRows({ rows }: { tableName: string, rows: Row[] }) {
			this.table.rows = rows;
		},
		async saveColumnSetting(columns: string | null | undefined) {
			this.$store.commit(`${this.storeUri}/${START_LOADING}`);
			await httpRequest.post(`Grid?gridId=${this.table.gridId}`, { userColumnsSettings: columns });
			this.$store.commit(`${this.storeUri}/${END_LOADING}`);
			if (!columns) {
				await this.schema();
			}
		},
		async getConfirmationForModifiedTable() {
			return this.showConfirm({
				message: this.LocalizeChyboveHlasky('PageLeaveMsg'),
				title: this.Localize('ShowMsg'),
				options: {
					confirmButtonText: this.LocalizeChyboveHlasky('PageLeaveLeaveBtn'),
					cancelButtonText: this.LocalizeChyboveHlasky('PageLeaveStayBtn'),
					type: 'warning',
				},
			});
		},
		validate(dialog = true) {
			let isFilterValid = false;
			this.validator((isValid?: boolean) => {
				if (isValid) {
					isFilterValid = true;
				} else if (dialog) {
					this.showErrorAlert({ message: 'InvalidForm' });
				}
			});

			return isFilterValid;
		},
	},
});
